import { useState } from "react";
import { useDispatch } from "react-redux";
import Button from "../button/Button";
import AuthTextInput from "../inputs/authTextInput/AuthTextInput";
import TextArea from "../inputs/textArea/TextArea";
import TextWrapper from "../textWrapper/TextWrapper";
import { showModal } from "../../features/statusModal/statusModal";
import styles from "./tournamentDetailsTemplateForm.module.css";

// Shared create/edit form for a tournament-details template. Both the Add and
// Edit pages render this; the parent owns what happens on submit (create vs.
// confirm-then-update) and just receives the assembled payload.
//
// The section/stage TITLES are fixed here (they mirror the app's fixed labels);
// the admin only adds bilingual POINTS under each. Semi-finals is always shown
// in the editor — it's type-agnostic — and the app hides it for solo
// tournaments at render time.

const STRUCTURE_SECTIONS = [
  { key: "qualification", label: "مرحلة التصفيات" },
  { key: "semifinals", label: "مرحلة نصف النهائي" },
  { key: "finals", label: "المرحلة النهائية" },
];

const RULES_SECTIONS = [
  { key: "identity", label: "قواعد الهوية" },
  { key: "behavior", label: "السلوك" },
  { key: "attendance", label: "الحضور" },
  { key: "fairPlay", label: "اللعب النزيه" },
];

const emptyGroup = (sections) =>
  sections.reduce((acc, section) => ({ ...acc, [section.key]: [] }), {});

// Normalize an incoming template group into { key: [{ ar, en }] } for only the
// known section keys, so editing an older/partial document can't crash the form.
const hydrateGroup = (sections, group = {}) =>
  sections.reduce((acc, section) => {
    const points = Array.isArray(group?.[section.key])
      ? group[section.key]
      : [];
    acc[section.key] = points.map((point) => ({
      ar: point?.ar || "",
      en: point?.en || "",
    }));
    return acc;
  }, {});

const TournamentDetailsTemplateForm = ({
  initialTemplate,
  submitLabel,
  onSubmit,
}) => {
  const dispatch = useDispatch();

  const [name, setName] = useState(initialTemplate?.name || "");
  const [structure, setStructure] = useState(() =>
    initialTemplate
      ? hydrateGroup(STRUCTURE_SECTIONS, initialTemplate.structure)
      : emptyGroup(STRUCTURE_SECTIONS),
  );
  const [rules, setRules] = useState(() =>
    initialTemplate
      ? hydrateGroup(RULES_SECTIONS, initialTemplate.rules)
      : emptyGroup(RULES_SECTIONS),
  );

  const setterFor = (group) =>
    group === "structure" ? setStructure : setRules;

  const addPoint = (group, key) =>
    setterFor(group)((prev) => ({
      ...prev,
      [key]: [...prev[key], { ar: "", en: "" }],
    }));

  const removePoint = (group, key, index) =>
    setterFor(group)((prev) => ({
      ...prev,
      [key]: prev[key].filter((_, itemIndex) => itemIndex !== index),
    }));

  const updatePoint = (group, key, index, lang, value) =>
    setterFor(group)((prev) => ({
      ...prev,
      [key]: prev[key].map((point, itemIndex) =>
        itemIndex === index ? { ...point, [lang]: value } : point,
      ),
    }));

  // Drop points that are entirely empty; flag any point that has only one of the
  // two languages filled (the backend requires both).
  const cleanGroup = (group) => {
    let hasPartial = false;
    const cleaned = {};
    Object.entries(group).forEach(([key, points]) => {
      const kept = [];
      points.forEach((point) => {
        const ar = String(point.ar || "").trim();
        const en = String(point.en || "").trim();
        if (ar === "" && en === "") return;
        if (ar === "" || en === "") hasPartial = true;
        kept.push({ ar, en });
      });
      cleaned[key] = kept;
    });
    return { cleaned, hasPartial };
  };

  const handleSubmit = () => {
    const trimmedName = name.trim();
    if (trimmedName === "") {
      dispatch(showModal({ message: "يرجى إدخال اسم القالب", status: "error" }));
      return;
    }

    const structureResult = cleanGroup(structure);
    const rulesResult = cleanGroup(rules);

    if (structureResult.hasPartial || rulesResult.hasPartial) {
      dispatch(
        showModal({
          message: "كل نقطة يجب أن تحتوي على النص بالعربية والإنجليزية",
          status: "error",
        }),
      );
      return;
    }

    onSubmit({
      name: trimmedName,
      structure: structureResult.cleaned,
      rules: rulesResult.cleaned,
    });
  };

  const renderSection = (group, section) => {
    const points = (group === "structure" ? structure : rules)[section.key];
    return (
      <section className={styles.section} key={section.key}>
        <div className={styles.sectionHeader}>
          <TextWrapper textStyle="textMid">{section.label}</TextWrapper>
          <div className={styles.sectionActionRow}>
            <Button
              size="circle"
              variant="surfaceVariant"
              functionallity={() => addPoint(group, section.key)}
            >
              <TextWrapper textStyle="textTitle">+</TextWrapper>
            </Button>
          </div>
        </div>
        <div className={styles.pointsList}>
          {points.length === 0 ? (
            <div className={styles.emptyHint}>
              <TextWrapper textStyle="textSmall">
                لا توجد نقاط بعد — اضغط + لإضافة نقطة
              </TextWrapper>
            </div>
          ) : (
            points.map((point, index) => (
              <div className={styles.pointRow} key={index}>
                <span className={styles.pointIndex}>{index + 1}</span>
                <div className={styles.pointFields}>
                  <div className={styles.pointField}>
                    <TextWrapper textStyle="textSmall">
                      النقطة بالعربية
                    </TextWrapper>
                    <TextArea
                      rows={2}
                      placeHolder="النقطة بالعربية"
                      value={point.ar}
                      onChange={(e) =>
                        updatePoint(
                          group,
                          section.key,
                          index,
                          "ar",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className={styles.pointField}>
                    <TextWrapper textStyle="textSmall">
                      النقطة بالإنجليزية
                    </TextWrapper>
                    <TextArea
                      rows={2}
                      placeHolder="Point in English"
                      value={point.en}
                      onChange={(e) =>
                        updatePoint(
                          group,
                          section.key,
                          index,
                          "en",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                </div>
                <button
                  type="button"
                  className={styles.removePointButton}
                  onClick={() => removePoint(group, section.key, index)}
                  title="حذف النقطة"
                  aria-label="حذف النقطة"
                >
                  ×
                </button>
              </div>
            ))
          )}
        </div>
      </section>
    );
  };

  return (
    <div className={styles.formLayout}>
      <div className={styles.fieldsWrapper}>
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <TextWrapper textStyle="textMid">اسم القالب</TextWrapper>
          </div>
          <div className={styles.nameFieldWrapper}>
            <AuthTextInput
              height="52px"
              width="100%"
              borderRadius="12px"
              placeholder="اسم القالب (للتمييز في القائمة)"
              bgColor={"grey"}
              value={name}
              setValue={setName}
            />
          </div>
        </section>

        <div className={styles.groupHeader}>
          <TextWrapper textStyle="textTitle">هيكل البطولة</TextWrapper>
        </div>
        {STRUCTURE_SECTIONS.map((section) =>
          renderSection("structure", section),
        )}

        <div className={styles.groupHeader}>
          <TextWrapper textStyle="textTitle">قوانين البطولة</TextWrapper>
        </div>
        {RULES_SECTIONS.map((section) => renderSection("rules", section))}
      </div>

      <div className={styles.submitWrapper}>
        <Button
          size="bigSize"
          variant="surfaceVariant"
          functionallity={handleSubmit}
        >
          <TextWrapper textStyle="textMid">{submitLabel}</TextWrapper>
        </Button>
      </div>
    </div>
  );
};

export default TournamentDetailsTemplateForm;
